// Small shared bits for pages. Features may import these from '../../app/ui'.
import { useEffect, useRef, type ReactNode } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { ApiError, useMe } from '../lib'

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <p className="muted" role="status">{what}…</p>
}

export function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? error.message || error.code : error instanceof Error ? error.message : 'Something went wrong'
  return (
    <div className="error-box" role="alert">
      {msg}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted empty">{children}</p>
}

/** Every company page shows this until the user has a company. */
export function NoCompany() {
  const { me } = useMe()
  const elsewhere = (me?.partners.length ?? 0) > 0 || me?.is_staff
  // A founder with no company starts the card request, which makes the company (founder, 2026-10-27).
  if (me?.user && !elsewhere) return <Navigate to="/card" replace />
  return (
    <div className="page narrow stack" data-testid="web-no-company">
      <div className="stack" style={{ gap: '0.5rem' }}>
        <h1>Set up your company</h1>
        <p className="muted" style={{ margin: 0 }}>Name your company, then send us your AI receipts. It takes about a minute.</p>
      </div>
      <div className="row">
        <Link className="button primary" to="/new" data-testid="web-create-company">Set up your company</Link>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Already on ernd at work? <Link to="/join">Join your team’s company</Link>.
        {elsewhere ? (
          <>
            {' '}Looking for something else?{' '}
            {me!.partners.length > 0 ? <Link to="/partner">Partner portal</Link> : null}
            {me!.partners.length > 0 && me!.is_staff ? ' or ' : null}
            {me!.is_staff ? <Link to="/staff">staff console</Link> : null}.
          </>
        ) : null}
      </p>
    </div>
  )
}

/** A pop-up over the page. Closes on ×, Esc or a click outside. */
export function Dialog({ label, onClose, children, testid = 'dialog' }: {
  label: string; onClose: () => void; children: ReactNode; testid?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Callers pass a new onClose each render; read it through a ref so focus moves to the dialog once, on open.
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current() }
    document.addEventListener('keydown', onKey)
    ref.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [])
  return (
    <div className="backdrop" data-testid={`${testid}-backdrop`} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={ref} data-testid={testid}>
        <button type="button" className="close" aria-label="Close" data-testid={`${testid}-close`} onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  )
}
